import { Region, WcpAuthorizer } from '@workday/wcp-sdk';
import { Audience } from 'shared/models/auth';
import { ExtendSubdomainService, subdomainApiPath } from 'shared/models/service';
import { getDevsiteFullPath } from 'shared/utils/devsite';
import { getSubdomainServiceUrl } from 'shared/utils/subdomainServiceUrl';
import routes from 'config/developerSite/routes';
import DevsiteCookies, { setCookie } from 'modules/common/DevsiteCookies';
import { DevToolsServerExecutor } from 'modules/common/requests/DevToolsServerRequestExecutor';
import { AppBuilderEndpoint, DevsiteApiExecutor } from 'modules/common/requests/ExecutorFactory';
import { RequestExecutor } from 'modules/common/requests/RequestExecutor';
import { clearSessionStoragePreservingInitialReferralContext } from 'modules/common/util/Analytics';
import wconsole from 'modules/common/util/wconsole';
import DevsiteEnv from '../DevsiteEnv';
import createPopup from '../utils/popup';
import { isHttpOnlyCookieAuth } from './authMode';
import { session } from './session';
import { getToken } from './token';

const POST_AUTH_REDIRECT_KEY = 'redirect_state';
export const AUTH_PLATFORM_KEY = 'auth_platform';
const DEFAULT_POST_AUTH_REDIRECT = routes.home;

export const EXPLORER_CLIENT_ID = DevsiteEnv.EXPLORER_CLIENT_ID;

/**
 * Checks the validity of a URI and navigates to it if successful, otherwise redirects to an error page.
 *
 * @param uri The URI to check and potentially navigate to.
 * @param errorPagePath The path to the error page (optional, defaults to '/errors/error.html').
 */
export async function changeWindowLocation(uri: string, errorPagePath = '/errors/error.html') {
  try {
    const response = await fetch(uri, { method: 'HEAD' }); // Use HEAD for a lightweight check
    if (response.ok) {
      window.location.assign(uri);
    } else {
      window.location.replace(getDevsiteFullPath(errorPagePath));
    }
  } catch (error) {
    wconsole.error('Error checking URI:', error);
    window.location.replace(getDevsiteFullPath(errorPagePath));
  }
}

/**
 * Initializes a PKCE auth flow within the current tab, and saves the generated state and code verifier
 * in session storage. If a post auth redirect url is passed, it is also saved to session storage.
 * See 'handlePkceResponseWithSession' below for details on the response phase of this PKCE flow.
 *
 * @param platform the platform to authorize the session for
 * @param redirectUri the redirect uri to receive the oauth response
 * @param redirectPathAfterAuth optional redirect for after auth has completed. Must be relative (e.g. '/console')
 */
export const authorizeSessionWithPKCE = async (
  platform: Audience,
  redirectUri: string,
  redirectPathAfterAuth?: string
) => {
  const { clientId, authProviderUrl } = getAuthProvider(platform);
  const tokenFetcher = new WcpAuthorizer(authProviderUrl, clientId, { usePkce: true });

  clearSessionStoragePreservingInitialReferralContext();
  // A PKCE authorizer always generates a code verifier; guard so we never persist an undefined value.
  if (!tokenFetcher.codeVerifier) {
    throw new Error('PKCE authorization flow did not produce a code verifier.');
  }
  // save the state and code verifier for post redirect code exchange
  sessionStorage.setItem(tokenFetcher.state, tokenFetcher.codeVerifier);
  sessionStorage.setItem(AUTH_PLATFORM_KEY, platform);
  // set redirect state if the path param is passed
  if (redirectPathAfterAuth) sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, decodeURIComponent(redirectPathAfterAuth));

  const uri = tokenFetcher.getAuthUri(redirectUri);
  await changeWindowLocation(uri);
};

export const authorizeSessionWithSecret = (platform: Audience, redirectUri: string, redirectPathAfterAuth?: string) => {
  const { clientId, authProviderUrl } = getAuthProvider(platform);
  const tokenFetcher = new WcpAuthorizer(authProviderUrl, clientId);

  clearSessionStoragePreservingInitialReferralContext();
  sessionStorage.setItem(AUTH_PLATFORM_KEY, platform);
  // set redirect state if the path param is passed
  if (redirectPathAfterAuth) sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, decodeURIComponent(redirectPathAfterAuth));

  const uri = tokenFetcher.getAuthUri(redirectUri);

  changeWindowLocation(uri);
};

const preparePkceAuthorizer = (oauthResp: OauthResponse): WcpAuthorizer => {
  const codeVerifier = sessionStorage.getItem(oauthResp.state);
  if (!codeVerifier) {
    throw new Error('PKCE code verifier not found for OAuth state.');
  }

  const authOptions = {
    usePkce: true,
    state: oauthResp.state,
    pkceCodeVerifier: codeVerifier,
  };

  const { clientId, authProviderUrl } = getAuthProvider(oauthResp.platform);
  return new WcpAuthorizer(authProviderUrl, clientId, authOptions);
};

/**
 * Handles the response phase of the PKCE flow. Takes in the query param response from the auth provider
 * as a string and conducts the token exchange with the auth provider. See 'authorizePkceWithSession'
 * above for the request phase of the PKCE flow. This function returns a promise that contains an object with
 * the response token, and an optional uri to redirect to. This function does not conduct the actual redirect
 * and leaves it up to the consumer.
 *
 * @param oauthResponse query string oauth response from the auth provider as a string (can begin '?')
 */
export const handlePkceResponseWithSession = async (oauthResponse: string) => {
  const oauthResp = await getPkceOauthResp(oauthResponse);

  const tokenFetcher = preparePkceAuthorizer(oauthResp);
  const token = await getAccessToken(tokenFetcher, oauthResp);
  const redirectState = getSessionRedirect();
  clearSessionStoragePreservingInitialReferralContext();

  return {
    token,
    redirectState,
  };
};

export const handlePkceResponseWithBff = async (oauthResponse: string) => {
  const oauthResp = await getPkceOauthResp(oauthResponse);
  const { clientId } = getAuthProvider(oauthResp.platform);
  await getAccessTokenWithBff(oauthResp, clientId);
  const redirectState = getSessionRedirect();
  clearSessionStoragePreservingInitialReferralContext();

  return redirectState;
};

export const handleOauthCodeResponseWithDts = async (oauthResponse: string) => {
  const oauthResp = await getPkceOauthResp(oauthResponse);

  const tokenFetcher = preparePkceAuthorizer(oauthResp);
  await getAccessTokenWithGateway(oauthResp, tokenFetcher);
  const redirectState = getSessionRedirect();
  clearSessionStoragePreservingInitialReferralContext();

  return redirectState;
};

/**
 * Fetch the desired redirect from session storage and fall back on the home page if it is not available.
 * Note, we only support relative urls. This prevents someone from trying to redirect
 * to, say (do not click) 'https://my.developersite.clone.com/timeforhacking'.
 */
export const getSessionRedirect = () => {
  const sessionRedirect = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
  return sessionRedirect && sessionRedirect[0] === '/' ? sessionRedirect : DEFAULT_POST_AUTH_REDIRECT;
};

export interface TenantPkceOptions {
  tenantAlias?: string;
  bypassSSO: boolean;
}
/**
 * Initiates a PKCE flow inside a popup. This function returns a promise that will contain the access
 * token if the authentication is successful. For this path, all oauth params are stored in memory
 * and not session storage. There is no need to save them to the browser because the parent page can
 * just listen for the response from the popup. See 'handlePkceResponseInPopup' for how the popup
 * handles the Oauth response.
 *
 * @param clientId the client id for the auth provider
 * @param tenantOptions optional tenant alias to authenticate to a tenant
 * @param authViaDevToolsServer whether to use devtools server to authenticate the tenant
 */
export const authorizeTenantWithPkceUsingPopup = async (
  clientId: string,
  tenantOptions?: TenantPkceOptions,
  authViaDevToolsServer?: boolean
): Promise<string> => {
  const authOptions: { usePkce: boolean; bypassSSO: boolean; tenantAlias?: string } = {
    usePkce: true,
    tenantAlias: undefined,
    bypassSSO: false,
  };

  if (tenantOptions) {
    const { tenantAlias, bypassSSO } = tenantOptions;
    authOptions.tenantAlias = tenantAlias;
    authOptions.bypassSSO = bypassSSO;
  }
  const redirect = `${window.location.protocol}//${window.location.host}${getDevsiteFullPath(routes.auth)}`;
  const sessionInfo = await session.getInfo();
  const authUri = sessionInfo.authBaseUri;
  const tokenFetcher = new WcpAuthorizer(authUri, clientId, authOptions);
  const popup = createPopup(tokenFetcher.getAuthUri(redirect, authOptions.bypassSSO), 'Authorize');
  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  return listenForPopupOauthResponseMessage(tokenFetcher, popup, authViaDevToolsServer);
};

export const DEVSITE_AUTH_POPUP_SIGNATURE = 'devsite-auth-popup';
export const FLOWBUILDER_AUTH_POPUP_SIGNATURE = 'flowbuilder-auth-popup';
export const SOAP_API_EXPLORER_AUTH_POPUP_SIGNATURE = 'soapApiExplorer-auth-popup';
export const NARWHAL_AUTH_POPUP_SIGNATURE = 'narwhal-auth-popup';
export const STUDIOBUILDER_AUTH_POPUP_SIGNATURE = 'studiobuilder-auth-popup';

/**
 * Convenience function for listening for an Oauth response from the addressbar when inside
 * a popup. This uses the Window.postMessage api but falls back on a traditional HTMLEvent
 * for browsers who do not support it. This function merely sends the oauth response query
 * string back to its parent for processing. See 'authorizePkceWithPopup' and
 * 'listenForPopupOauthResponseMessage' for details on popup initialization and listening
 * for the event this function dispatches.
 */
export const handlePkceResponseInPopup = (popupSignature: string) => {
  const data = {
    source: popupSignature,
    payload: window.location.search,
  };

  // see https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
  window.opener.postMessage(data, DevsiteEnv.DEVSITE_URL);
  window.close();
};

/**
 * Listens for a child window message that contains a PKCE Oauth response query string. Upon receipt,
 * this conducts the actual access token exchange with the PKCE params.
 *
 * Returns a promise that resolves to the access token.
 *
 * @param tokenFetcher a WcpDeveloperTokenFetcher instance from the wcp-sdk. The same instance as used in the request.
 * @param popup The popup window reference
 * @param authViaDevToolsServer whether to use devtools server to authenticate the tenant
 */
const listenForPopupOauthResponseMessage = (
  tokenFetcher: WcpAuthorizer,
  popup: Window,
  authViaDevToolsServer: boolean = false
): Promise<string> =>
  new Promise((resolve, reject) => {
    // flag for message retrieval
    // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
    let messageReceived: string = null;

    const listener = async (event: MessageEvent) => {
      // ensure that we only accept messages from us, get outta here Oracle.
      if (event.origin !== window.location.origin) {
        return;
      }
      if (!event.data.source || event.data.source !== DEVSITE_AUTH_POPUP_SIGNATURE) return;

      window.removeEventListener('message', listener);

      // notify all listeners that the message has been received
      messageReceived = event.data.payload;

      try {
        const oauthResp = await getPkceOauthResp(messageReceived);
        if (authViaDevToolsServer) {
          await exchangeCodeForAccessTokenOnServer(tokenFetcher, oauthResp, resolve);
        } else {
          const token = await getAccessToken(tokenFetcher, oauthResp);
          resolve(token);
        }
      } catch (err) {
        reject(err);
      }
    };

    // look in '/auth' (root of the project, index.tsx) to see where the data is getting sent.
    // Also see the 'handleOauthResponseInPopup' function above. For more information, see
    // https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
    window.addEventListener('message', listener, false);

    // keep an eye on the popup state
    const popupCheck = setInterval(() => {
      // if a message was received, stop checking
      if (messageReceived) {
        clearInterval(popupCheck);
        // event data should be a query string
      } else if (popup && popup.closed) {
        // stop listening and report abort if the popup is ever closed.
        clearInterval(popupCheck);

        // set a timeout to minimize race conditions. Sometimes the popup
        // closed event can beat the message send.
        setTimeout(() => {
          if (!messageReceived) {
            // clean up event listener if necessary
            // window.removeEventListener('message', listener);
            reject('Authentication was cancelled.');
          }
        }, 500);
      }
    }, 500);
  });

/**
 * Switches the devsite session to the desired region if a user has an account in that region.
 *
 * @param region
 */
export async function switchRegion(region: string) {
  const sessionInfo = await session.getInfo();
  if (isHttpOnlyCookieAuth(sessionInfo.platform)) {
    await DevsiteApiExecutor.post('/v1/token-exchange', JSON.stringify({ region }));
  } else {
    const authorizer = new WcpAuthorizer(DevsiteEnv.OCTOPAAS_URL, DevsiteEnv.OCTOPAAS_CLIENT, { usePkce: true });
    // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
    const token = await authorizer.exchangeToken(getToken(), region, true);
    setCookie(DevsiteCookies.WDAY_ACCESS_TOKEN, token);
  }
  await session.notifyTabsOfNewSession();
}

export interface BaseOauthResponse {
  /** state param that the initial request was called with. */
  state: string;
  /** This parameter contains the authorization code which the client will later exchange for an access token. */
  code: string;
  /** The region id for the login */
  region: string;
  /** error tag if an error occurred */
  error?: string;
  /**
   * detailed description to explain the error tag if present. According to Oauth, This parameter is intended
   * for the developer to understand the error, and is not meant to be displayed to the end user.
   */

  error_description?: string;
}
export interface OauthResponse extends BaseOauthResponse {
  /** platform the platform to authorize the session for */
  platform: Audience;
}

export interface DevToolsOauthRequest extends BaseOauthResponse {
  /** Optional code verifier */
  codeVerifier?: string;
}

/**
 * Convenience function for translating a query string to a PKCE Oauth response object from Octopaas
 * @param oauthResponse query string oauth response from the auth provider as a string (can begin '?')
 */
const resolveOauthRegion = (regionParam: string | null, sessionRegion: string): string => {
  if (regionParam) return decodeURIComponent(regionParam);
  return sessionRegion;
};

const getPkceOauthResp = async (oauthResponse: string): Promise<OauthResponse> => {
  const params = new URLSearchParams(oauthResponse);
  const sessionRegion = await session.getInfo();
  const platform = resolveAudience(params.get('platform') || sessionStorage.getItem(AUTH_PLATFORM_KEY));
  return {
    // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
    state: params.get('state'),
    // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
    code: params.get('code'),
    platform,
    // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
    error: params.get('error'),
    // @ts-expect-error DEVEXTOOLS-1514: fix legacy strictNullChecks rule violation
    // eslint-disable-next-line camelcase -- OAuth standard uses snake_case
    error_description: params.get('error_description'),
    region: resolveOauthRegion(params.get('region'), sessionRegion.scope.region),
  };
};

/**
 * Validates Oauth response with the initial request and fetches the access token. This functionality
 * may later be added directly to the WcpDeveloperTokenFetcher.prototype.getAccessToken function in the
 * wcp-sdk.
 * @param tokenFetcher a WcpDeveloperTokenFetcher instance from the wcp-sdk
 * @param params an OauthResponse object resembling the PKCE response from the auth provider
 */
const getAccessToken = async (tokenFetcher: WcpAuthorizer, params: OauthResponse) => {
  validateOauthResp(params);

  return tokenFetcher.getAccessTokenWithPkce(params.code, params.state, params.region);
};

/**
 * This proxies the pkce token fetch step through the developer site web server, which will fetch the token on
 * its behalf and set it as an HTTP-Only cookie. The client will NOT receive the token in this flow.
 *
 * @param oauthResp the oauth response from octopaas
 * @param pkceParams the initial pkce params for authentication
 * @param clientId the client id for authentication
 */
const getAccessTokenWithBff = async (oauthResp: OauthResponse, clientId: string) => {
  validateOauthResp(oauthResp);

  const payload = {
    ...oauthResp,
    clientId,
  };

  return DevsiteApiExecutor.post('/v1/token', JSON.stringify(payload));
};

const getAccessTokenWithGateway = async (oauthResp: OauthResponse, authorizer: WcpAuthorizer) => {
  validateOauthResp(oauthResp);

  // Authorizer baseURL is already …/auth; OctopaasConnector expects a bare host and would double the scheme/path.
  const regions = await authorizer.getRegions();
  const regionInfo = regions.data.find((region: Region) => region.name === oauthResp.region);

  const payload = {
    state: oauthResp.state,
    code: oauthResp.code,
    region: oauthResp.region,
    codeVerifier: authorizer.codeVerifier,
  };

  const dtsUrl = DevsiteEnv.LOCAL_DEV_GATEWAY_PROXY
    ? `${subdomainApiPath}/devtools`
    : `${getSubdomainServiceUrl(
        ExtendSubdomainService.DEVTOOLS_SERVER,
        DevsiteEnv.SUBDOMAIN_API_BASE_URL,
        regionInfo?.alias ?? undefined
      )}/devtools`;

  const executor = new RequestExecutor(dtsUrl, { defaultHeaders: { 'Content-Type': 'application/json' } });
  // Cross-origin DTS Set-Cookie (Domain=developer.*) is ignored unless credentials are included.
  return executor.post(AppBuilderEndpoint.TOKEN_V2, JSON.stringify(payload), undefined, undefined, {
    credentials: 'include',
  });
};

/**
 * This proxies the pkce token fetch step through devtools-server, which will fetch the token on
 * its behalf and set it as an HTTP-Only cookie. The client will NOT receive the token in this flow.
 *
 * @param tokenFetcher a WcpDeveloperTokenFetcher instance from the wcp-sdk
 * @param oauthResp an OauthResponse object resembling the PKCE response from the auth provider
 * @param resolve resolves the Promise with the access token received from the Oauth flow
 */
const exchangeCodeForAccessTokenOnServer = async (
  tokenFetcher: WcpAuthorizer,
  oauthResp: OauthResponse,
  resolve: (value: string | PromiseLike<string>) => void
) => {
  const tokenAuthPayload: DevToolsOauthRequest = {
    state: oauthResp.state,
    code: oauthResp.code,
    region: oauthResp.region,
    codeVerifier: tokenFetcher.codeVerifier,
  };

  try {
    const dtServerAuthResponse = await DevToolsServerExecutor.post(
      AppBuilderEndpoint.TOKEN,
      JSON.stringify(tokenAuthPayload)
    );

    if (!dtServerAuthResponse.body?.access_token) {
      throw new Error('Invalid server response: missing access_token');
    }

    return resolve(dtServerAuthResponse.body.access_token);
  } catch (error) {
    wconsole.error('DevTools server token exchange failed:', error);
    throw error; // Re-throw to be handled by caller
  }
};

export const getAuthClientId = async (authViaDevToolsServer?: boolean): Promise<string> => {
  if (!authViaDevToolsServer) return DevsiteEnv.EXPLORER_CLIENT_ID;
  try {
    const clientId = await DevToolsServerExecutor.get(AppBuilderEndpoint.TENANT_CLIENT_ID);
    if (!clientId.body?.clientId) throw new Error('Tenant client ID not found in server response');
    return clientId.body.clientId;
  } catch (error) {
    wconsole.error('Error getting auth client id:', error);
    throw error;
  }
};

const validateOauthResp = (oauthResp: OauthResponse) => {
  if (oauthResp.error) {
    const errorDescription = oauthResp.error_description || oauthResp.error;
    throw errorDescription;
  }
};

const getAuthProvider = (platform: Audience) => {
  const isOpenDeveloper = platform === Audience.OPEN_DEVELOPER && DevsiteEnv.HAS_OPEN_DEVELOPER_ENVIRONMENT;
  const clientId = isOpenDeveloper ? DevsiteEnv.OCTOPAAS_CLIENT_OPEN_DEV! : DevsiteEnv.OCTOPAAS_CLIENT;
  const authProviderUrl = isOpenDeveloper ? DevsiteEnv.OCTOPAAS_URL_OPEN_DEV! : DevsiteEnv.OCTOPAAS_URL;
  return {
    clientId,
    authProviderUrl,
  };
};

/**
 * resolves the correct platform audience from a string value.
 * @param platform the platform to resolve the audience for
 * @returns the audience
 */
export const resolveAudience = (platform: string | undefined | null): Audience => {
  if (DevsiteEnv.HAS_OPEN_DEVELOPER_ENVIRONMENT && platform === Audience.OPEN_DEVELOPER) {
    return Audience.OPEN_DEVELOPER;
  }
  // fallback to enterprise when platform is not valid or empty
  return Audience.ENTERPRISE;
};
